'use client';

import Link from 'next/link';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForwardOutlined';
import ApiIcon from '@mui/icons-material/ApiOutlined';
import GroupIcon from '@mui/icons-material/GroupOutlined';
import HistoryIcon from '@mui/icons-material/HistoryOutlined';
import StorageIcon from '@mui/icons-material/StorageOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutlined';
import PlayArrowIcon from '@mui/icons-material/PlayArrowOutlined';
import VisibilityIcon from '@mui/icons-material/VisibilityOutlined';
import AddIcon from '@mui/icons-material/AddOutlined';
import RemoveIcon from '@mui/icons-material/RemoveOutlined';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import MoveUpIcon from '@mui/icons-material/MoveUpOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopyOutlined';
import CheckBoxIcon from '@mui/icons-material/CheckBoxOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import LibraryBooksOutlinedIcon from '@mui/icons-material/LibraryBooksOutlined';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import Navigation from '../../components/Navigation';
import RelatedPages from '../../components/RelatedPages';

const operations = [
  { id: 'op-add', num: 1, title: 'Add Operation', desc: 'Adds a value to an object or inserts it into an array.', code: '{ "op": "add", "path": "/name", "value": "John Doe" }', href: '/operations/add' },
  { id: 'op-remove', num: 2, title: 'Remove Operation', desc: 'Removes a value from an object or array.', code: '{ "op": "remove", "path": "/email" }', href: '/operations/remove' },
  { id: 'op-replace', num: 3, title: 'Replace Operation', desc: 'Replaces a value. Equivalent to a remove followed by an add.', code: '{ "op": "replace", "path": "/age", "value": 30 }', href: '/operations/replace' },
  { id: 'op-move', num: 4, title: 'Move Operation', desc: 'Moves a value from one location to another.', code: '{ "op": "move", "from": "/old-path", "path": "/new-path" }', href: '/operations/move' },
  { id: 'op-copy', num: 5, title: 'Copy Operation', desc: 'Copies a value from one location to another.', code: '{ "op": "copy", "from": "/source", "path": "/destination" }', href: '/operations/copy' },
  { id: 'op-test', num: 6, title: 'Test Operation', desc: 'Tests that a value at the target location is equal to a specified value.', code: '{ "op": "test", "path": "/version", "value": "1.0" }', href: '/operations/test' },
];

const concepts = [
  {
    title: 'JSON Patch and Arrays',
    description: 'Learn array indexes, append syntax, operation ordering, and safe patterns for changing JSON arrays.',
    href: '/docs/json-patch-array',
  },
  {
    title: 'JSON Pointer',
    description: 'Understand RFC 6901 paths, escaping rules, root values, and how pointers target values in a JSON document.',
    href: '/docs/json-pointer',
  },
  {
    title: 'JSON Patch and Nested Objects',
    description: 'Update deep properties, create intermediate structure, and move whole subtrees with full pointer paths.',
    href: '/docs/json-patch-nested-objects',
  },
  {
    title: 'JSON Patch in REST APIs',
    description: 'HTTP PATCH, application/json-patch+json, ETag concurrency, status codes, and client code.',
    href: '/docs/json-patch-rest-api',
  },
  {
    title: 'JSON Patch vs JSON Diff',
    description: 'A diff shows what changed; a patch is the operations that make the change. When to use each.',
    href: '/docs/json-patch-vs-json-diff',
  },
];

const howTos = [
  {
    title: 'How to Generate a JSON Patch',
    description: 'Turn a before and after document into an RFC 6902 patch, in the browser or in code.',
    href: '/docs/how-to-generate-json-patch',
  },
  {
    title: 'How to Apply a JSON Patch',
    description: 'Execute a patch safely: sequential semantics, validation, and test preconditions.',
    href: '/docs/how-to-apply-json-patch',
  },
  {
    title: 'JSON Patch Errors',
    description: 'Why operations fail - missing paths, out-of-bounds indexes, bad pointers, test mismatches - and how to fix each.',
    href: '/docs/json-patch-errors',
  },
  {
    title: 'JSON Patch Validator',
    description: 'Check a patch is well-formed, then verify it against a real document in the browser.',
    href: '/validate',
  },
];

const guides = [
  {
    title: 'JSON Patch in Python',
    description: 'Apply and generate patches with the jsonpatch library.',
    href: '/guides/json-patch-in-python',
  },
  {
    title: 'JSON Patch in JavaScript',
    description: 'Apply and compare JSON documents with fast-json-patch.',
    href: '/guides/json-patch-in-javascript',
  },
  {
    title: 'JSON Patch in Go',
    description: 'Apply and generate patches with evanphx/json-patch.',
    href: '/guides/json-patch-in-go',
  },
  {
    title: 'JSON Patch in Java',
    description: 'Apply and generate patches with Jackson and java-json-tools.',
    href: '/guides/json-patch-in-java',
  },
  {
    title: 'JSON Patch vs JSON Merge Patch',
    description: 'Compare RFC 6902 with RFC 7396 and choose the right format.',
    href: '/guides/json-patch-vs-json-merge-patch',
  },
];

const useCases = [
  { icon: ApiIcon, title: 'REST API Updates', desc: 'Send PATCH requests with only the changes instead of the entire resource. Reduces bandwidth and improves performance, especially for large objects.' },
  { icon: GroupIcon, title: 'Real-time Collaboration', desc: 'Sync changes between multiple users editing the same document. Used by collaborative editors like Google Docs, Notion, and Figma.' },
  { icon: HistoryIcon, title: 'Version Control', desc: 'Track incremental changes to configuration files, database records, or any JSON data structure effectively.' },
  { icon: StorageIcon, title: 'Database Operations', desc: 'Update only modified fields in NoSQL databases like MongoDB, CouchDB, or DynamoDB.' },
];

const bestPractices = [
  { bold: 'Always validate patches', rest: ' before applying them to prevent security issues' },
  { bold: 'Handle failures explicitly', rest: ' because evaluation stops on error and rollback depends on the implementation' },
  { bold: 'Test operations', rest: ' to verify conditions before making changes' },
  { bold: 'Order matters', rest: ' — operations are applied sequentially' },
  { bold: 'Use meaningful paths', rest: ' that are easy to understand and maintain' },
];

const sidebarOps = [
  { icon: AddIcon, label: 'Add Operation', href: '#op-add' },
  { icon: RemoveIcon, label: 'Remove Operation', href: '#op-remove' },
  { icon: SyncAltIcon, label: 'Replace Operation', href: '#op-replace' },
  { icon: MoveUpIcon, label: 'Move Operation', href: '#op-move' },
  { icon: ContentCopyIcon, label: 'Copy Operation', href: '#op-copy' },
  { icon: CheckBoxIcon, label: 'Test Operation', href: '#op-test' },
];

export default function DocsPage() {
  const headingColor = 'var(--fg)';
  const mutedColor = 'var(--fg-muted)';
  const borderColor = 'var(--border)';
  const surfaceBg = 'var(--surface)';
  const codeBg = 'var(--code-bg)';
  const headerStripBg = 'var(--bg-subtle)';

  return (
    <>
      <Navigation activePage="docs" />

      <Container maxWidth="lg" sx={{ py: { xs: 4, sm: 6 }, px: { xs: 2, sm: 3 } }}>
        {/* Breadcrumb */}
        <Box component="nav" sx={{ display: 'flex', mb: 4, fontSize: '0.875rem', color: mutedColor }}>
          <Link href="/" style={{ textDecoration: 'none', color: mutedColor }}>
            <Typography component="span" sx={{ '&:hover': { color: 'var(--accent)' }, transition: 'color var(--duration-fast)', fontSize: '0.875rem' }}>Home</Typography>
          </Link>
          <Typography component="span" sx={{ mx: 1, fontSize: '0.875rem', color: mutedColor }}>/</Typography>
          <Typography component="span" sx={{ color: headingColor, fontSize: '0.875rem' }}>Documentation</Typography>
        </Box>

        {/* Hero */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h1" sx={{ fontSize: { xs: '2rem', sm: '2.5rem', lg: '3rem' }, fontWeight: 700, mb: 2, letterSpacing: '-0.03em', color: headingColor }}>
            JSON Patch Documentation
          </Typography>
          <Typography sx={{ fontSize: '1.125rem', color: mutedColor, maxWidth: '48rem', mb: 3, lineHeight: 1.7 }}>
            Everything you need to know about RFC 6902 JSON Patch operations. A format for describing changes to a JSON document.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {['RFC 6902', 'IETF Standard', `Last updated: ${new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`].map((label) => (
              <Box key={label} sx={{
                display: 'inline-flex', alignItems: 'center', px: 1.5, py: 0.5,
                borderRadius: 'var(--radius-full)', fontSize: 'var(--font-size-xs)', fontWeight: 500,
                backgroundColor: 'var(--bg-muted)',
                color: 'var(--fg-muted)',
                border: '1px solid var(--border)',
              }}>
                {label}
              </Box>
            ))}
          </Box>
        </Box>

        {/* Main Content + Sidebar */}
        <Grid container spacing={6}>
          {/* Main Column */}
          <Grid item xs={12} lg={8}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {/* What is JSON Patch? */}
              <Paper sx={{
                p: { xs: 3, lg: 4 }, borderRadius: '12px', border: '1px solid var(--border)',
                backgroundColor: surfaceBg, boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
              }}>
                <Typography variant="h2" sx={{ fontSize: '1.5rem', fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1, color: headingColor }}>
                  <InfoOutlinedIcon sx={{ color: 'var(--accent)' }} />
                  What is JSON Patch?
                </Typography>
                <Typography sx={{ mb: 2, color: mutedColor, lineHeight: 1.7 }}>
                  JSON Patch (RFC 6902) is a format for describing changes to a JSON document.
                  It defines a JSON document structure for expressing a sequence of operations to apply to a target JSON document.
                </Typography>
                <Typography sx={{ color: mutedColor, lineHeight: 1.7 }}>
                  Instead of sending entire objects when making updates, JSON Patch lets you send only the specific changes needed.
                  This makes it incredibly efficient for APIs, real-time applications, and data synchronization.
                </Typography>
              </Paper>

              {/* Documentation Index */}
              <Box>
                <Typography variant="h2" sx={{
                  fontSize: '1.875rem', fontWeight: 700, mb: 1, color: headingColor,
                }}>
                  Browse Documentation
                </Typography>
                <Typography sx={{ color: mutedColor, mb: 4, lineHeight: 1.7 }}>
                  Start with the core concepts, then use the operation reference or follow a practical guide.
                </Typography>

                <Typography variant="h3" sx={{ fontSize: '1.25rem', fontWeight: 700, mb: 2, color: headingColor }}>
                  Concepts
                </Typography>
                <Grid container spacing={3} sx={{ mb: 5 }}>
                  {concepts.map((concept) => (
                    <Grid item xs={12} sm={6} key={concept.href}>
                      <Link href={concept.href} style={{ textDecoration: 'none' }}>
                        <Paper sx={{
                          p: 3, height: '100%', borderRadius: '12px', border: '1px solid var(--border)',
                          backgroundColor: surfaceBg, boxShadow: 'none',
                          transition: 'border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease',
                          '&:hover': { borderColor: 'var(--accent)', transform: 'translateY(-2px)', boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)' },
                        }}>
                          <Typography variant="h4" sx={{ fontSize: '1.1rem', fontWeight: 700, mb: 1, color: headingColor, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                            {concept.title}
                            <ArrowForwardIcon sx={{ color: 'var(--accent)', fontSize: '20px', flexShrink: 0 }} />
                          </Typography>
                          <Typography variant="body2" sx={{ color: mutedColor, lineHeight: 1.6 }}>
                            {concept.description}
                          </Typography>
                        </Paper>
                      </Link>
                    </Grid>
                  ))}
                </Grid>

                <Typography variant="h3" sx={{ fontSize: '1.25rem', fontWeight: 700, mb: 2, color: headingColor }}>
                  How-Tos and Troubleshooting
                </Typography>
                <Grid container spacing={3} sx={{ mb: 5 }}>
                  {howTos.map((howTo) => (
                    <Grid item xs={12} sm={6} key={howTo.href}>
                      <Link href={howTo.href} style={{ textDecoration: 'none' }}>
                        <Paper sx={{
                          p: 3, height: '100%', borderRadius: '12px', border: '1px solid var(--border)',
                          backgroundColor: surfaceBg, boxShadow: 'none',
                          transition: 'border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease',
                          '&:hover': { borderColor: 'var(--accent)', transform: 'translateY(-2px)', boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)' },
                        }}>
                          <Typography variant="h4" sx={{ fontSize: '1.1rem', fontWeight: 700, mb: 1, color: headingColor, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                            {howTo.title}
                            <ArrowForwardIcon sx={{ color: 'var(--accent)', fontSize: '20px', flexShrink: 0 }} />
                          </Typography>
                          <Typography variant="body2" sx={{ color: mutedColor, lineHeight: 1.6 }}>
                            {howTo.description}
                          </Typography>
                        </Paper>
                      </Link>
                    </Grid>
                  ))}
                </Grid>

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
                  <Typography variant="h3" sx={{ fontSize: '1.25rem', fontWeight: 700, color: headingColor }}>
                    Practical Guides
                  </Typography>
                  <Link href="/guides" style={{ textDecoration: 'none' }}>
                    <Typography variant="body2" sx={{ color: 'var(--accent)', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}>
                      View all guides
                    </Typography>
                  </Link>
                </Box>
                <Grid container spacing={3}>
                  {guides.map((guide) => (
                    <Grid item xs={12} sm={6} key={guide.href}>
                      <Link href={guide.href} style={{ textDecoration: 'none' }}>
                        <Paper sx={{
                          p: 3, height: '100%', borderRadius: '12px', border: '1px solid var(--border)',
                          backgroundColor: surfaceBg, boxShadow: 'none',
                          transition: 'border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease',
                          '&:hover': { borderColor: 'var(--accent)', transform: 'translateY(-2px)', boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)' },
                        }}>
                          <Typography variant="h4" sx={{ fontSize: '1.05rem', fontWeight: 700, mb: 1, color: headingColor, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                            {guide.title}
                            <ArrowForwardIcon sx={{ color: 'var(--accent)', fontSize: '18px', flexShrink: 0 }} />
                          </Typography>
                          <Typography variant="body2" sx={{ color: mutedColor, lineHeight: 1.6 }}>
                            {guide.description}
                          </Typography>
                        </Paper>
                      </Link>
                    </Grid>
                  ))}
                </Grid>
              </Box>

              {/* Operations Section */}
              <Box>
                <Typography variant="h2" sx={{
                  fontSize: '1.875rem', fontWeight: 700, mb: 4, pb: 2,
                  borderBottom: `1px solid ${borderColor}`, color: headingColor,
                }}>
                  JSON Patch Operations
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {operations.map((op) => (
                    <Paper key={op.id} id={op.id} sx={{
                      borderRadius: '12px', border: '1px solid var(--border)', overflow: 'hidden',
                      backgroundColor: surfaceBg, boxShadow: 'none',
                      transition: 'box-shadow 0.3s ease',
                      '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.08)' },
                    }}>
                      {/* Header strip */}
                      <Box sx={{
                        p: 3, borderBottom: `1px solid ${borderColor}`,
                        backgroundColor: headerStripBg,
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      }}>
                        <Typography variant="h3" sx={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--accent)', display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box component="span" sx={{
                            fontFamily: 'var(--font-mono), monospace', fontSize: '0.875rem',
                            backgroundColor: 'var(--accent-muted)', px: 1, py: 0.5, borderRadius: '4px',
                          }}>
                            {op.num}.
                          </Box>
                          {op.title}
                        </Typography>
                        <Link href={op.href} style={{ textDecoration: 'none', color: mutedColor }}>
                          <ArrowForwardIcon sx={{ fontSize: '20px' }} />
                        </Link>
                      </Box>
                      {/* Body */}
                      <Box sx={{ p: 3 }}>
                        <Typography sx={{ color: mutedColor, mb: 2 }}>{op.desc}</Typography>
                        <Box sx={{
                          backgroundColor: codeBg, p: 2, borderRadius: '8px',
                          fontFamily: 'var(--font-mono), monospace', fontSize: '0.875rem',
                          overflowX: 'auto', border: '1px solid var(--border)',
                          color: 'var(--fg)',
                        }}>
                          {op.code}
                        </Box>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              </Box>

              {/* Use Cases */}
              <Paper sx={{
                p: { xs: 3, lg: 4 }, borderRadius: '12px', border: '1px solid var(--border)',
                backgroundColor: surfaceBg,
              }}>
                <Typography variant="h2" sx={{ fontSize: '1.5rem', fontWeight: 700, mb: 3, color: headingColor }}>
                  Real-World Use Cases
                </Typography>
                <Grid container spacing={4}>
                  {useCases.map((uc) => (
                    <Grid item xs={12} sm={6} key={uc.title}>

                      <Typography variant="h3" sx={{ fontSize: '1.1rem', fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1, color: headingColor }}>
                        <uc.icon sx={{ color: 'var(--accent)', fontSize: '18px' }} />
                        {uc.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: mutedColor, lineHeight: 1.6 }}>
                        {uc.desc}
                      </Typography>
                    </Grid>
                  ))}
                </Grid>
              </Paper>

              {/* Best Practices */}
              <Paper sx={{
                p: { xs: 3, lg: 4 }, borderRadius: '12px', border: '1px solid var(--border)',
                backgroundColor: surfaceBg, position: 'relative', overflow: 'hidden',
              }}>
                <Box sx={{ position: 'absolute', top: 0, right: 0, p: 2, opacity: 0.06, pointerEvents: 'none' }}>
                  <VerifiedOutlinedIcon sx={{ fontSize: '120px', color: 'var(--accent)' }} />
                </Box>
                <Typography variant="h2" sx={{ fontSize: '1.5rem', fontWeight: 700, mb: 3, color: headingColor }}>
                  Best Practices
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {bestPractices.map((bp, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                      <CheckCircleIcon sx={{ color: 'var(--success)', fontSize: '22px', mt: '2px', flexShrink: 0 }} />
                      <Typography sx={{ color: mutedColor }}>
                        <Box component="span" sx={{ fontWeight: 700, color: headingColor }}>{bp.bold}</Box>
                        {bp.rest}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Box>
          </Grid>

          {/* Sidebar */}
          <Grid item xs={12} lg={4}>
            <Paper sx={{
              p: 3, borderRadius: '12px', border: '1px solid var(--border)',
              backgroundColor: surfaceBg, position: 'sticky', top: 88,
            }}>
              <Typography variant="h3" sx={{ fontWeight: 700, fontSize: '1.1rem', mb: 2, color: headingColor }}>
                Quick Links
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 4 }}>
                <Link href="/" style={{ textDecoration: 'none' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'var(--accent)', fontSize: '0.875rem', '&:hover': { textDecoration: 'underline' } }}>
                    <PlayArrowIcon sx={{ fontSize: '18px' }} />
                    <Typography variant="body2" sx={{ color: 'var(--accent)' }}>Try the Generator</Typography>
                  </Box>
                </Link>
                <Link href="/examples" style={{ textDecoration: 'none' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'var(--accent)', fontSize: '0.875rem', '&:hover': { textDecoration: 'underline' } }}>
                    <VisibilityIcon sx={{ fontSize: '18px' }} />
                    <Typography variant="body2" sx={{ color: 'var(--accent)' }}>View Examples</Typography>
                  </Box>
                </Link>
                <Link href="/docs/json-patch-array" style={{ textDecoration: 'none' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'var(--accent)', fontSize: '0.875rem', '&:hover': { textDecoration: 'underline' } }}>
                    <MenuBookOutlinedIcon sx={{ fontSize: '18px' }} />
                    <Typography variant="body2" sx={{ color: 'var(--accent)' }}>JSON Patch and Arrays</Typography>
                  </Box>
                </Link>
                <Link href="/docs/json-pointer" style={{ textDecoration: 'none' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'var(--accent)', fontSize: '0.875rem', '&:hover': { textDecoration: 'underline' } }}>
                    <LibraryBooksOutlinedIcon sx={{ fontSize: '18px' }} />
                    <Typography variant="body2" sx={{ color: 'var(--accent)' }}>JSON Pointer Guide</Typography>
                  </Box>
                </Link>
              </Box>

              <Typography variant="h3" sx={{ fontWeight: 700, fontSize: '1.1rem', mb: 2, color: headingColor }}>
                Operation Deep Dives
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {sidebarOps.map((op) => (
                  <a key={op.href} href={op.href} style={{ textDecoration: 'none' }}>
                    <Box sx={{
                      display: 'flex', alignItems: 'center', gap: 1,
                      color: mutedColor, fontSize: '0.875rem',
                      transition: 'color 0.15s', '&:hover': { color: 'var(--accent)' },
                    }}>
                      <op.icon sx={{ fontSize: '16px' }} />
                      <Typography variant="body2" sx={{ color: 'inherit' }}>{op.label}</Typography>
                    </Box>
                  </a>
                ))}
              </Box>

              <Typography variant="h3" sx={{ fontWeight: 700, fontSize: '1.1rem', mt: 4, mb: 2, color: headingColor }}>
                Popular Libraries
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {[
                  { lang: 'JavaScript:', lib: 'fast-json-patch' },
                  { lang: 'Python:', lib: 'jsonpatch' },
                  { lang: 'Java:', lib: 'jackson-json-patch' },
                  { lang: 'Go:', lib: 'json-patch' },
                  { lang: 'PHP:', lib: 'json-patch-php' },
                ].map((item) => (
                  <Box key={item.lang} sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: headingColor }}>{item.lang}</Typography>
                    <Typography variant="body2" sx={{ color: mutedColor }}>{item.lib}</Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Related Pages */}
        <RelatedPages currentPage="docs" showOperations={true} maxItems={6} />
      </Container>
    </>
  );
}
